import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/cn'
import type { IconName } from '../Icon'
import { Icon } from '../Icon'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'solid' | 'bare'

interface CommonProps {
  children: ReactNode
  variant?: ButtonVariant
  /** Glyph after the label. */
  icon?: IconName
  /** Glyph before the label (the phone on "Book a call"). */
  leadingIcon?: IconName
  fullWidth?: boolean
  className?: string
}

interface LinkButtonProps extends CommonProps { to: string; href?: never }
interface AnchorButtonProps extends CommonProps {
  href: string
  to?: never
  /**
   * Saves the target instead of opening it.
   *
   * A downloading link must not also carry `target="_blank"`: the two
   * together open a tab that immediately closes itself, which on some
   * browsers cancels the download with it.
   */
  download?: boolean
}
interface ActionButtonProps
  extends CommonProps,
    Omit<ComponentPropsWithoutRef<'button'>, keyof CommonProps> {
  to?: never
  href?: never
}

type ButtonProps = LinkButtonProps | AnchorButtonProps | ActionButtonProps

/**
 * The site's only call-to-action element.
 *
 * It picks its element from the props given — `to` for internal routes,
 * `href` for external, neither for an action — so callers express intent
 * rather than restyling a bare anchor.
 *
 * Styling follows the reference build — a flat tinted rectangle. The lit
 * "orb" material is reserved for the card arrows.
 */
export const Button = (props: ButtonProps) => {
  const {
    children,
    variant = 'primary',
    icon,
    leadingIcon,
    fullWidth = false,
    className,
    ...rest
  } = props

  const classes = cn(
    variant !== 'bare' && styles.button,
    // Anything with button material catches the cursor's light. `bare` is a
    // text link with no surface to light, so it stays out.
    variant !== 'bare' && 'torch',
    styles[variant],
    fullWidth && styles.fullWidth,
    className,
  )

  const content = (
    <>
      {leadingIcon && <Icon name={leadingIcon} size={14} className={styles.icon} />}
      <span>{children}</span>
      {icon && <Icon name={icon} size={12} className={styles.icon} />}
    </>
  )

  if ('to' in props && props.to !== undefined) {
    return (
      <Link to={props.to} className={classes}>
        {content}
      </Link>
    )
  }

  if ('href' in props && props.href !== undefined) {
    const isDownload = 'download' in props && props.download === true

    return (
      <a
        href={props.href}
        className={classes}
        {...(isDownload
          ? { download: '' }
          : { target: '_blank', rel: 'noreferrer noopener' })}
      >
        {content}
      </a>
    )
  }

  const buttonRest = rest as ComponentPropsWithoutRef<'button'>
  return (
    <button type="button" {...buttonRest} className={classes}>
      {content}
    </button>
  )
}
